import { useState, useEffect, useRef } from 'react';
import { useSupabase } from './useSupabase';
import { useAuth } from './useAuth';

interface RawRole {
  role: string;
}

export const useUserRoles = () => {
  const { userId, loading: authLoading } = useAuth();
  const supabase = useSupabase();
  const isLoaded = !authLoading;

  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const hasFetchedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchRoles = async () => {
      // Wait for auth to be fully loaded
      if (!isLoaded || authLoading) {
        return;
      }

      // Reset if user changed
      if (lastUserIdRef.current !== userId) {
        lastUserIdRef.current = userId;
        hasFetchedRef.current = false;
      }

      if (!userId) {
        setRoles([]);
        setLoading(false);
        return;
      }

      // Prevent duplicate fetches
      if (hasFetchedRef.current) {
        return;
      }

      hasFetchedRef.current = true;

      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId);

        if (error) throw error;

        const rawData = (data || []) as unknown as RawRole[];
        const rolesList = rawData.map(r => r.role);
        setRoles(rolesList);
      } catch (error) {
        console.error('[useUserRoles] Error fetching user roles:', error);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };

    // Fire-and-forget: fetchRoles handles its own errors and resets state.
    void fetchRoles();
  }, [userId, authLoading, isLoaded, supabase]);

  return {
    roles,
    loading,
    isManager: roles.some(r => ['manager', 'gm', 'admin', 'global_admin', 'tenant_admin'].includes(r)),
    isAdmin: roles.some(r => ['admin', 'global_admin'].includes(r)),
    isGlobalAdmin: roles.includes('global_admin'),
    isTenantAdmin: roles.includes('tenant_admin'),
    isRep: roles.some(r => ['rep', 'sales_rep'].includes(r)),
    hasRole: (role: string) => roles.includes(role),
  };
};
